import axios from "axios";
import api from "./axiosConfig";

export const MAX_PREPPO_CERTIFICATE_SIZE_BYTES = 20 * 1024 * 1024;
export const UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

export function extractApiErrorMessage(
  error: unknown,
  fallback: string
): string {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | { message?: string | string[] }
      | undefined;
    const msg = data?.message;
    if (Array.isArray(msg) && msg.length > 0) {
      return msg.filter((m) => typeof m === "string").join(", ") || fallback;
    }
    if (typeof msg === "string" && msg.trim()) {
      return msg;
    }
  }
  if (error instanceof Error && error.message.trim()) {
    return error.message;
  }
  return fallback;
}

export const ACCEPTED_CERTIFICATE_MIME_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
];

const MIME_BY_EXTENSION: Record<string, string> = {
  ".pdf": "application/pdf",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
};

function getFileExtension(fileName: string): string {
  const dotIndex = fileName.lastIndexOf(".");
  return dotIndex >= 0 ? fileName.slice(dotIndex).toLowerCase() : "";
}

export function resolveCertificateMimeType(file: File): string | null {
  if (file.type && ACCEPTED_CERTIFICATE_MIME_TYPES.includes(file.type)) {
    return file.type;
  }
  return MIME_BY_EXTENSION[getFileExtension(file.name)] || null;
}

async function verifyPendingObjectExists(objectKey: string): Promise<boolean> {
  try {
    const response = await api.post("/agents/pending-upload/verify", {
      objectKey,
    });
    return Boolean(response.data?.exists);
  } catch {
    return false;
  }
}

/**
 * Presigned POST to S3. When the bucket CORS lacks POST, S3 may still return
 * 201 while the browser surfaces a Network Error — verify via API in that case.
 */
async function postFileToPresignedUrl(
  uploadUrl: string,
  fields: Record<string, string>,
  file: File,
  objectKey: string
): Promise<void> {
  const formData = new FormData();
  for (const [key, value] of Object.entries(fields)) {
    formData.append(key, value);
  }
  formData.append("file", file);

  try {
    await axios.post(uploadUrl, formData, {
      timeout: UPLOAD_TIMEOUT_MS,
      transformRequest: [
        (data, headers) => {
          if (headers && typeof headers === "object") {
            delete (headers as Record<string, unknown>)["Content-Type"];
          }
          return data;
        },
      ],
      validateStatus: (status) => status === 201 || status === 204,
    });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      throw new Error("Arquivo excede o limite de 20MB.");
    }
    // No HTTP response → often CORS masking a successful 201 Created.
    if (axios.isAxiosError(error) && !error.response) {
      if (await verifyPendingObjectExists(objectKey)) {
        return;
      }
    }
    throw error;
  }
}

/** Presigned POST no S3 durante o cadastro (antes do agent existir). */
export async function uploadPendingPreppoCertificate(
  file: File
): Promise<string> {
  const mimeType = resolveCertificateMimeType(file);
  if (!mimeType) {
    throw new Error("Tipo de arquivo inválido. Envie PDF, JPG ou PNG.");
  }
  if (file.size > MAX_PREPPO_CERTIFICATE_SIZE_BYTES) {
    throw new Error("Arquivo excede o limite de 20MB.");
  }

  try {
    const initResponse = await api.post(
      "/agents/preppo-certificate/upload-url",
      {
        fileName: file.name,
        mimeType,
        size: file.size,
      }
    );

    const { uploadUrl, fields, objectKey } = initResponse.data as {
      uploadUrl: string;
      fields: Record<string, string>;
      objectKey: string;
    };

    await postFileToPresignedUrl(uploadUrl, fields, file, objectKey);

    return objectKey;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.status === 400) {
      throw new Error("Arquivo excede o limite de 20MB.");
    }
    throw new Error(
      extractApiErrorMessage(
        error,
        "Erro ao enviar certificado. Tente novamente."
      )
    );
  }
}
